// src/BlitzHowTo/TutorialOverlay.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Fade,
  Portal,
  Backdrop,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  CheckCircle as CompleteIcon,
} from '@mui/icons-material';
import { styled, alpha, keyframes } from '@mui/material/styles';
import { useTutorial } from './tutorialContext';
import { iconMap } from './TutorialDialog';

// Animations
const spotlightPulse = keyframes`
  0%, 100% { 
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
  }
  50% { 
    box-shadow: 0 0 0 10px rgba(59, 130, 246, 0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

// Spotlight highlight for target element
const Spotlight = styled(Box)(({ theme }) => ({
  position: 'absolute',
  pointerEvents: 'none',
  border: `3px solid ${theme.palette.primary.main}`,
  borderRadius: theme.spacing(1),
  animation: `${spotlightPulse} 2s infinite`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: theme.zIndex.modal + 2,
}));

// Floating dialog card
const TutorialCard = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  maxWidth: 400,
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.3)}`,
  animation: `${fadeIn} 0.3s ease-out`,
  zIndex: theme.zIndex.modal + 3,
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
    : theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

// Arrow pointer
const Arrow = styled(Box)(({ theme, placement }) => {
  const size = 12;
  const positions = {
    top: {
      bottom: -size,
      left: '50%',
      transform: 'translateX(-50%) rotate(45deg)',
    },
    bottom: {
      top: -size,
      left: '50%',
      transform: 'translateX(-50%) rotate(45deg)',
    },
    left: {
      right: -size,
      top: '50%',
      transform: 'translateY(-50%) rotate(45deg)',
    },
    right: {
      left: -size,
      top: '50%',
      transform: 'translateY(-50%) rotate(45deg)',
    },
  };

  return {
    position: 'absolute',
    width: size * 2,
    height: size * 2,
    background: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    borderRight: 'none',
    borderBottom: 'none',
    ...positions[placement || 'top'],
  };
});

export const TutorialOverlay = () => {
  const { 
    activeTutorial, 
    currentStep, 
    isActive,
    isMobile,
    nextStep,
    previousStep,
    skipTutorial,
  } = useTutorial();

  const [targetRect, setTargetRect] = useState(null);
  const [cardPosition, setCardPosition] = useState({ top: 0, left: 0 });
  const cardRef = useRef(null);

  const step = activeTutorial?.steps[currentStep];
  const totalSteps = activeTutorial?.steps.length || 0;
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;

  // ═══════════════════════════════════════════════════════════
  // SCROLL LOCKING - Disable scrolling when tutorial is active
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (!isActive) return;

    console.log('[Tutorial] 🔒 Locking scroll');
    
    // Save current scroll position
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    
    // Apply scroll lock styles
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalLeft = document.body.style.left;
    const originalWidth = document.body.style.width;
    
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = `-${scrollX}px`;
    document.body.style.width = '100%';

    // Cleanup function to restore scrolling
    return () => {
      console.log('[Tutorial] 🔓 Unlocking scroll');
      
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.left = originalLeft;
      document.body.style.width = originalWidth;
      
      // Restore scroll position
      window.scrollTo(scrollX, scrollY);
    };
  }, [isActive]);

  // Calculate positions when step changes
  useEffect(() => {
    if (!step || !isActive) {
      setTargetRect(null);
      return;
    }

    const calculatePositions = () => {
      // Center placement (no target)
      if (!step.target || step.placement === 'center') {
        setTargetRect(null);
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        setCardPosition({
          top: viewportHeight / 2,
          left: viewportWidth / 2,
          transform: 'translate(-50%, -50%)',
        });
        return;
      }

      // Find target element
      const targetEl = document.querySelector(step.target);
      if (!targetEl) {
        console.warn(`Tutorial target not found: ${step.target}`);
        setTargetRect(null);
        return;
      }

      // Get target position
      const rect = targetEl.getBoundingClientRect();
      const padding = 8;
      
      setTargetRect({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });

      // Calculate card position based on placement
      const cardEl = cardRef.current;
      if (!cardEl) return;

      const cardRect = cardEl.getBoundingClientRect();
      const gap = 20;
      const offset = step.offset || { x: 0, y: 0 };

      let position = {};

      switch (step.placement) {
        case 'top':
          position = {
            top: rect.top - cardRect.height - gap + offset.y,
            left: rect.left + rect.width / 2 + offset.x,
            transform: 'translateX(-50%)',
          };
          break;
        case 'bottom':
          position = {
            top: rect.bottom + gap + offset.y,
            left: rect.left + rect.width / 2 + offset.x,
            transform: 'translateX(-50%)',
          };
          break;
        case 'left':
          position = {
            top: rect.top + rect.height / 2 + offset.y,
            left: rect.left - cardRect.width - gap + offset.x,
            transform: 'translateY(-50%)',
          };
          break;
        case 'right':
          position = {
            top: rect.top + rect.height / 2 + offset.y,
            left: rect.right + gap + offset.x,
            transform: 'translateY(-50%)',
          };
          break;
        default:
          position = {
            top: rect.bottom + gap,
            left: rect.left,
          };
      }

      // Ensure card stays in viewport
      const maxLeft = window.innerWidth - cardRect.width - 20;
      const maxTop = window.innerHeight - cardRect.height - 20;
      
      if (position.left > maxLeft) position.left = maxLeft;
      if (position.left < 20) position.left = 20;
      if (position.top > maxTop) position.top = maxTop;
      if (position.top < 20) position.top = 20;

      setCardPosition(position);
    };

    // Initial calculation
    calculatePositions();

    // Recalculate on resize (but not on scroll since we're locked)
    window.addEventListener('resize', calculatePositions);

    return () => {
      window.removeEventListener('resize', calculatePositions);
    };
  }, [step, currentStep, isActive]);

  // Don't render on mobile
  if (isMobile) {
    return null;
  }

  if (!isActive || !activeTutorial || !step) {
    return null;
  }

  const IconComponent = iconMap[step.icon] || iconMap.star;

  return (
    <Portal>
      {/* Backdrop */}
      <Backdrop
        open={isActive}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: alpha('#000', 0.5),
          backdropFilter: 'blur(2px)',
        }}
        onClick={(e) => {
          // Only close if clicking backdrop, not spotlight or card
          if (e.target === e.currentTarget) {
            skipTutorial();
          }
        }}
      />

      {/* Spotlight on target element */}
      {targetRect && (
        <Spotlight
          sx={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
        />
      )}

      {/* Tutorial Card */}
      <TutorialCard
        ref={cardRef}
        sx={cardPosition}
        elevation={8}
      >
        {/* Arrow pointer */}
        {step.placement && step.placement !== 'center' && (
          <Arrow placement={step.placement} />
        )}

        {/* Close button */}
        <IconButton
          onClick={skipTutorial}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Progress */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconComponent 
              sx={{ 
                fontSize: 32,
                color: 'primary.main',
              }} 
            />
            <Typography variant="caption" color="text.secondary">
              Step {currentStep + 1} of {totalSteps}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              height: 4,
              borderRadius: 2,
              backgroundColor: alpha('#000', 0.1),
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
                background: (theme) => 
                  `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              },
            }}
          />
        </Box>

        {/* Content */}
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700,
            mb: 1.5,
            fontSize: '1.1rem',
          }}
        >
          {step.title}
        </Typography>

        <Typography 
          variant="body2" 
          sx={{ 
            mb: 3,
            lineHeight: 1.6,
            color: 'text.secondary',
          }}
        >
          {step.content}
        </Typography>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
          <Button
            variant="text"
            size="small"
            onClick={skipTutorial}
            sx={{ textTransform: 'none' }}
          >
            Skip Tour
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={previousStep}
              disabled={isFirstStep}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>

            <Button
              variant="contained"
              endIcon={isLastStep ? <CompleteIcon /> : <NextIcon />}
              onClick={nextStep}
              sx={{
                textTransform: 'none',
                background: (theme) => 
                  `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              }}
            >
              {isLastStep ? 'Complete' : 'Next'}
            </Button>
          </Box>
        </Box>
      </TutorialCard>
    </Portal>
  );
};